// Ch97 · PKT-0792 PR 2 · ONE decision-identity rule, shared by both surfaces.
//
// 🔴 WHY THIS IS A LIB AND NOT A SECOND COPY.
//
// ContentPage has carried these semantics since PKT-0427/0533. The Socials tab
// needs exactly the same ones the moment its verdict button is wired, and the
// Director's instruction was explicit: port them, do not re-invent, and if they
// cannot be reused as-is then EXTRACT — because two copies of an idempotency
// rule drift, and the copy that drifts is the one that files a second decision
// on Grant's content.
//
// THE FAILURE THIS GUARDS, stated as the thing that actually happens:
//   Grant approves a reel. The server accepts it. The response never arrives —
//   the connection drops, the laptop sleeps, the request times out. The client
//   cannot tell "it never landed" from "it landed and I did not hear". Retrying
//   with a FRESH request id files a SECOND decision. Retrying with the SAME id
//   is idempotent: the server recognises it and answers with the first outcome.
//
// ⇒ "unknown" is a FOURTH state, not a flavour of `error`:
//      loading    we are fetching
//      error      it failed, and we KNOW it did not land
//      unknown    it MIGHT have landed. Retry is safe ONLY with the same identity.
//      busy       in flight right now; do not submit again
//
// This module is pure — no React, no network — so every rule below is testable
// without a DOM, and both callers are provably running the same one.

/** The identity of a decision in flight. A retry MUST reuse all three. */
export interface PendingDecision {
  /** The idempotency key the server dedupes on. Never regenerated for a retry. */
  requestId: string;
  /** The verdict as first submitted. A different one is refused, not queued. */
  verdict: string;
  /** The note as first submitted. A retry replays it; it never takes a new one. */
  note: string | null;
}

export type DecisionOutcome =
  /** The server answered. Whatever it said, the decision is settled. */
  | "settled"
  /** The request failed in a way that proves it did not land. */
  | "failed"
  /** We do not know whether it landed. Keep the identity; offer Retry. */
  | "unknown";

export interface BeginResult {
  /** null ⇒ REFUSED; do not send. `reason` says why. */
  decision: PendingDecision | null;
  reason?: "verdict-changed" | "in-flight";
}

/**
 * Start or RETRY a decision.
 *
 * `pending` is what (if anything) is already in flight for this item.
 * `inFlight` is whether a request is on the wire RIGHT NOW (the busy guard).
 *
 * 🔑 THE THREE RULES, each of them ContentPage's, not new:
 *   1. a retry reuses the SAME requestId — ContentPage.tsx:475
 *      `const requestId = pending?.requestId ?? createRequestId();`
 *   2. a retry replays the SAME note and ignores any new one — :477-479
 *      "A retry replays the original note; only a first attempt takes a new one."
 *   3. a DIFFERENT verdict while one is pending is REFUSED — :474
 *      `if (pending && pending.verdict !== verdict) return;`
 *      You cannot turn an in-flight approve into a reject by clicking the other
 *      button; the first decision has to settle first.
 */
export function beginDecision(
  pending: PendingDecision | undefined,
  verdict: string,
  note: string | null,
  newRequestId: () => string,
  inFlight = false
): BeginResult {
  // Rule 3 first: a verdict change is refused even on a retry.
  if (pending && pending.verdict !== verdict) {
    return { decision: null, reason: "verdict-changed" };
  }
  // The busy guard. A second click while the first is on the wire sends nothing.
  // This is what makes a double-click ONE request rather than two identical ones
  // — the same requestId would make a second call harmless to the DATA, but it
  // is still a second call, and the button should not have been live.
  if (inFlight) {
    return { decision: null, reason: "in-flight" };
  }
  return {
    decision: {
      requestId: pending?.requestId ?? newRequestId(),
      verdict,
      // Rule 2: a retry replays; only a first attempt takes the caller's words.
      note: pending ? pending.note : (note ?? "").trim() || null,
    },
  };
}

/**
 * What to do with the pending identity once the attempt resolves.
 *
 * Returns true when the identity must be KEPT so a retry can reuse it — which
 * is the `unknown` case and ONLY the unknown case. `failed` drops it because a
 * request we know did not land needs no idempotency key; a fresh attempt is a
 * fresh decision and should be.
 */
export function keepIdentity(outcome: DecisionOutcome): boolean {
  return outcome === "unknown";
}
